import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useCurrency } from '@/contexts/CurrencyContext'
import { toastHelpers } from '@/lib/toast-helpers'
import type { OnlineSubMethod } from '@/types'
import type { Custody, RiderTicket } from '@/types/rider'

// Collect payment at the door (F-DLV-N1). The backend records it through the
// counter's own payment path; the rider picks HOW it was paid and — for an
// online transfer — WHOSE account it landed in, which decides whether the
// rider now holds the money.

type Tender =
  | { kind: 'cash' }
  | { kind: 'wallet'; slug: string | null; label: string }
  | { kind: 'card' }

function tenderKey(t: Tender): string {
  return t.kind === 'wallet' ? `wallet:${t.slug ?? ''}` : t.kind
}

export function RiderCollectDialog({
  open,
  onOpenChange,
  ticket,
  due,
  subMethods,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  ticket: RiderTicket
  due: number
  subMethods: OnlineSubMethod[]
}) {
  const qc = useQueryClient()
  const { formatCurrency } = useCurrency()

  const tenders: Tender[] = [
    { kind: 'cash' },
    ...(subMethods.length > 0
      ? subMethods.map((m) => ({ kind: 'wallet' as const, slug: m.slug, label: m.label }))
      : [{ kind: 'wallet' as const, slug: null, label: 'Online transfer' }]),
    { kind: 'card' },
  ]

  const [tender, setTender] = useState<Tender>(tenders[0])
  const [into, setInto] = useState<'rider_wallet' | 'business_wallet'>('rider_wallet')
  const [amount, setAmount] = useState('')
  const [reference, setReference] = useState('')
  // Tax differs by tender, so the server re-prices the order to the tender
  // chosen (F-MM-N6) and refuses the old figure (order_reprice_conflict). The
  // rider sees the new total here and confirms it with the customer.
  const [repriced, setRepriced] = useState<{ from: number; to: number } | null>(null)

  useEffect(() => {
    if (open) {
      setTender({ kind: 'cash' })
      setInto('rider_wallet')
      setAmount(due > 0 ? String(due) : '')
      setReference('')
      setRepriced(null)
    }
  }, [open, due])

  const effectiveDue = repriced ? Math.max(0, Math.round((repriced.to - ticket.paid) * 100) / 100) : due
  const parsed = Number(amount)
  const amountOk = Number.isFinite(parsed) && parsed > 0
  const needsRef = tender.kind !== 'cash'
  const canSubmit = amountOk && (!needsRef || reference.trim() !== '')

  const collect = useMutation({
    mutationFn: () => {
      const custody: Custody = tender.kind === 'cash' ? 'cash' : tender.kind === 'card' ? 'card_terminal' : into
      return apiClient.riderCollect(ticket.assignment_id!, {
        payment_method: tender.kind === 'wallet' ? 'online' : tender.kind,
        payment_sub_method: tender.kind === 'wallet' ? tender.slug : null,
        amount: parsed,
        reference_number: needsRef ? reference.trim() : null,
        custody,
      })
    },
    onSuccess: () => {
      toastHelpers.success('Payment recorded', `${formatCurrency(parsed)} on order #${ticket.order_number}`)
      qc.invalidateQueries({ queryKey: ['rider'] })
      onOpenChange(false)
    },
    onError: (err: unknown) => {
      const e = err as { code?: string; message?: string; payload?: { previous_total?: number; new_total?: number } }
      const to = e.payload?.new_total
      if (e.code === 'order_reprice_conflict' && typeof to === 'number') {
        const paidSoFar = ticket.paid
        setRepriced({ from: e.payload?.previous_total ?? ticket.total, to })
        setAmount(String(Math.max(0, Math.round((to - paidSoFar) * 100) / 100)))
        return
      }
      toastHelpers.error('Could not record payment', e?.message || 'Please try again.')
    },
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Collect payment</DialogTitle>
          <DialogDescription>
            Order #{ticket.order_number} · {formatCurrency(due)} to collect
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>How did the customer pay?</Label>
            <div className="grid grid-cols-2 gap-2">
              {tenders.map((t) => {
                const active = tenderKey(t) === tenderKey(tender)
                return (
                  <Button
                    key={tenderKey(t)}
                    type="button"
                    variant={active ? 'default' : 'outline'}
                    onClick={() => {
                      setTender(t)
                      if (repriced) {
                        setRepriced(null)
                        setAmount(due > 0 ? String(due) : '')
                      }
                    }}
                  >
                    {t.kind === 'cash' ? 'Cash' : t.kind === 'card' ? 'Card machine' : t.label}
                  </Button>
                )
              })}
            </div>
          </div>

          {tender.kind === 'wallet' && (
            <div className="space-y-1.5">
              <Label>Which account did it go to?</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button type="button" variant={into === 'rider_wallet' ? 'default' : 'outline'} onClick={() => setInto('rider_wallet')}>
                  My own wallet
                </Button>
                <Button type="button" variant={into === 'business_wallet' ? 'default' : 'outline'} onClick={() => setInto('business_wallet')}>
                  Restaurant’s account
                </Button>
              </div>
              {into === 'rider_wallet' && (
                <p className="text-xs text-muted-foreground">You’ll owe this until you hand it over at the counter.</p>
              )}
            </div>
          )}

          {repriced && (
            <p className="rounded-md bg-amber-50 px-2 py-1.5 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              Paying this way changes the bill from {formatCurrency(repriced.from)} to{' '}
              <span className="font-semibold">{formatCurrency(repriced.to)}</span> (different tax rate). Tell the
              customer, then record the new amount.
            </p>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="rider-collect-amount">Amount</Label>
            <Input
              id="rider-collect-amount"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
            />
            {amountOk && parsed < effectiveDue && (
              <p className="text-xs text-amber-700 dark:text-amber-400">
                {formatCurrency(Math.round((effectiveDue - parsed) * 100) / 100)} will still be owed after this.
              </p>
            )}
          </div>

          {needsRef && (
            <div className="space-y-1.5">
              <Label htmlFor="rider-collect-ref">{tender.kind === 'card' ? 'Slip number' : 'Transaction ID'}</Label>
              <Input id="rider-collect-ref" value={reference} onChange={(e) => setReference(e.target.value)} />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => collect.mutate()} disabled={!canSubmit || collect.isPending}>
            Record {amountOk ? formatCurrency(parsed) : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
